import { useMemo } from 'react';

import { matchBuilderSizeGuide } from './matchBuilderSizeGuide';

/**
 * Matches pre-fetched Builder size guide entries against product categories,
 * optionally boosted by default category and tag matching.
 *
 * @param {Object} params
 * @param {Array<Object>} params.sizeGuides - Builder size guide entries to match against
 * @param {Array<number>} params.categoryIdList - Product category IDs to match
 * @param {number} [params.defaultCategoryId] - Product's default category ID for bonus scoring
 * @param {Array<number>} [params.tagIdList] - Product tag IDs; +1 relevance if any overlap with entry's tags
 * @returns {Object} - { builderContent, matchCount }
 */
export const useBuilderSizeGuide = ({
  sizeGuides,
  categoryIdList,
  defaultCategoryId,
  tagIdList,
}) => {
  // Find best matching size guide based on category overlap and optional relevance signals
  const matchedContent = useMemo(() => {
    // Skip if no entries or no categories to match
    if (sizeGuides?.length === 0 || categoryIdList?.length === 0) {
      return null;
    }

    return matchBuilderSizeGuide(sizeGuides, categoryIdList, {
      defaultCategoryId,
      tagIdList,
    });
  }, [sizeGuides, categoryIdList, defaultCategoryId, tagIdList]);

  // Calculate match count for debugging/analytics
  const matchCount = useMemo(() => {
    if (!matchedContent || !categoryIdList) {
      return 0;
    }

    const entryCategories = matchedContent?.data?.categories;
    if (!Array.isArray(entryCategories)) {
      return null;
    }

    // Count how many product categories match the size guide categories
    // Note: entryCategories are in Builder's nested format, will be normalized by intersection
    const normalizeBuilderIds = (builderIds) => {
      return builderIds
        .map((item) => {
          const value = item?.category?.value || item?.value || item;
          const numValue =
            typeof value === 'string' ? parseInt(value, 10) : value;
          return typeof numValue === 'number' && !isNaN(numValue)
            ? numValue
            : null;
        })
        .filter((id) => id !== null);
    };

    const normalizedCategories = normalizeBuilderIds(entryCategories);
    return normalizedCategories.filter((cat) => categoryIdList.includes(cat))
      .length;
  }, [matchedContent, categoryIdList]);

  return {
    builderContent: matchedContent,
    matchCount,
  };
};
